import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Star, Loader2, Video, Image as ImageIcon, X } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { adminListMedia, adminCreateMedia, adminUpdateMedia, adminDeleteMedia, adminUploadImage } from "../lib/api";

const CATEGORIES = [
  { slug: "histoire", label: "À propos — Notre histoire" },
  { slug: "equipe", label: "À propos — Notre équipe" },
  { slug: "fournisseurs", label: "À propos — Nos fournisseurs" },
  { slug: "stock", label: "À propos — Notre stock" },
  { slug: "preparation", label: "À propos — Préparation commandes" },
  { slug: "envois", label: "À propos — Preuves d'envoi (colis TikTok Shop)" },
  { slug: "certificats", label: "À propos — Certificats & conformité" },
  { slug: "qualite", label: "À propos — Engagements qualité" },
  { slug: "galerie", label: "À propos — Galerie principale" },
  { slug: "tiktok", label: "À propos — TikTok Shop" },
  { slug: "backstage", label: "Coulisses / Atelier" },
  { slug: "product", label: "Présentation produit" },
  { slug: "packaging", label: "Emballage" },
  { slug: "shipping", label: "Expédition" },
  { slug: "founder", label: "Fondatrice" },
];

const AdminMedia = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [form, setForm] = useState({ title: "", description: "", category: "backstage", featured: false });

  const load = async () => {
    setLoading(true);
    try { setItems(await adminListMedia()); } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const uploaded = await adminUploadImage(file, setProgress);
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const url = `${base}${uploaded.url}`;
      const media = await adminCreateMedia({
        title: form.title || file.name.replace(/\.[^.]+$/, ""),
        description: form.description,
        media_url: url,
        media_type: uploaded.is_video ? "video" : "photo",
        category: form.category,
        featured: form.featured,
      });
      setItems([media, ...items]);
      setForm({ title: "", description: "", category: "backstage", featured: false });
      toast.success(`${uploaded.is_video ? "Vidéo" : "Photo"} publiée`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'upload");
    } finally { setUploading(false); setProgress(0); }
  };

  const toggle = async (m, field) => {
    try {
      const updated = await adminUpdateMedia(m.id, { [field]: !m[field] });
      setItems(items.map(x => x.id === m.id ? updated : x));
    } catch { toast.error("Erreur"); }
  };
  const remove = async (m) => {
    if (!window.confirm("Supprimer ce média ?")) return;
    try { await adminDeleteMedia(m.id); setItems(items.filter(x => x.id !== m.id)); toast.success("Supprimé"); }
    catch { toast.error("Erreur"); }
  };

  return (
    <AdminLayout title="Coulisses & Contenus" subtitle="Publiez vos vidéos et photos depuis votre téléphone. Elles apparaissent instantanément sur la page d'accueil, la galerie et les fiches produit.">
      {/* Upload panel */}
      <div className="border border-[color:var(--gold)]/30 p-6 md:p-8 mb-10">
        <p className="label-caps text-[color:var(--gold)] text-[10px] mb-6">Publier un nouveau contenu</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-line" data-testid="media-title" />
          <input placeholder="Description (facultative)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-line md:col-span-2" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-line bg-transparent">
            {CATEGORIES.map(c => <option key={c.slug} value={c.slug} className="bg-[#0a0a0a]">{c.label}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 mb-6 label-caps text-[10px] text-white/60 cursor-pointer">
          <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[color:var(--gold)]" />
          Mettre en avant sur la page d'accueil
        </label>

        <label
          data-testid="media-upload-zone"
          className={`block border border-dashed p-8 md:p-12 text-center cursor-pointer transition-colors ${uploading ? "opacity-60 pointer-events-none" : "border-white/20 hover:border-[color:var(--gold)] hover:bg-[color:var(--gold)]/5"}`}
        >
          {uploading ? (
            <>
              <Loader2 size={28} className="mx-auto text-[color:var(--gold)] animate-spin mb-3" />
              <p className="label-caps text-[10px] text-[color:var(--gold)]">Téléversement · {progress}%</p>
              <div className="mt-3 max-w-xs mx-auto h-1 bg-white/10 overflow-hidden">
                <div className="h-full bg-[color:var(--gold)]" style={{ width: `${progress}%` }} />
              </div>
            </>
          ) : (
            <>
              <Upload size={28} className="mx-auto text-[color:var(--gold)] mb-3" />
              <p className="label-caps text-[10px] text-white/80 mb-2">Cliquez ou glissez-déposez votre fichier</p>
              <p className="text-white/40 text-xs">Photos (JPG, PNG, WEBP · 5 MB max) · Vidéos (MP4, MOV, WEBM · 100 MB max)</p>
              <p className="text-white/40 text-xs mt-1">💡 Sur mobile : bouton d'appareil photo pour filmer directement</p>
            </>
          )}
          <input type="file" accept="image/*,video/*" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" />
        </label>
      </div>

      {/* Grid of media */}
      {loading ? <p className="label-caps text-white/40">Chargement...</p> : items.length === 0 ? (
        <p className="text-white/40 italic text-center py-16">Aucun média pour l'instant. Publiez votre premier contenu ci-dessus.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m) => (
            <div key={m.id} data-testid={`media-${m.id.slice(0,8)}`} className={`border ${m.active ? "border-white/10" : "border-white/5 opacity-50"} bg-[#0a0a0a] group relative`}>
              <div className="aspect-square bg-black overflow-hidden relative">
                {m.media_type === "video" ? (
                  <video src={m.media_url} className="w-full h-full object-cover" muted playsInline />
                ) : (
                  <img src={m.media_url} alt={m.title} className="w-full h-full object-cover" />
                )}
                <div className="absolute top-2 left-2 label-caps text-[9px] bg-black/70 text-[color:var(--gold)] px-2 py-1 flex items-center gap-1">
                  {m.media_type === "video" ? <Video size={10} /> : <ImageIcon size={10} />}
                  {CATEGORIES.find(c => c.slug === m.category)?.label || m.category}
                </div>
                {m.featured && <div className="absolute top-2 right-2 bg-[color:var(--gold)] text-black p-1"><Star size={10} fill="currentColor" /></div>}
              </div>
              <div className="p-3">
                <p className="font-serif text-sm text-white truncate">{m.title}</p>
                <div className="flex items-center justify-between mt-3">
                  <button onClick={() => toggle(m, "featured")} title="Mettre en avant" className={`text-xs ${m.featured ? "text-[color:var(--gold)]" : "text-white/40 hover:text-[color:var(--gold)]"}`}>
                    <Star size={13} fill={m.featured ? "currentColor" : "none"} />
                  </button>
                  <button onClick={() => toggle(m, "active")} title={m.active ? "Cacher" : "Publier"} className="label-caps text-[9px] text-white/50 hover:text-white">
                    {m.active ? "Publié" : "Caché"}
                  </button>
                  <button onClick={() => remove(m)} className="text-red-400 hover:text-red-300"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminMedia;
